import {
  LimitTradeConfirmationToast,
  OrderCancelToast,
  QuoteBookTradeConfirmationToast,
  TradeConfirmationToast,
} from '@features/dashboard/widgets/trading/TradeConfirmationToast';
import { TradeErrorToast } from '@features/dashboard/widgets/trading/TradeErrorToast';
import { Typography } from '@mui/material';
import { OtcCancelOrderResponse, OtcOrderType, OtcQuoteOrderResponse } from '@protos/v2/otcQuote';
import { Order } from '@protos/v2/order';
import { OrderResponse } from '@shared/protos/trading';
import '@shared/styles/toastStyles.scss';
import _ from 'lodash';
import { Id, Slide, toast, ToastOptions } from 'react-toastify';

const confirmationToastOptions: ToastOptions = {
  position: 'top-center',
  autoClose: 1500,
  closeButton: false,
  closeOnClick: false,
  draggable: false,
  className: 'flux-trade-confirmation',
  hideProgressBar: true,
  transition: Slide,
};
class ToastifyService {
  private _unauthorizedToastId: Id = 'unauthorized-error';
  private _currentUserId: string | undefined;

  public setCurrentUserId(userId: string | undefined): void {
    this._currentUserId = userId;
  }

  public hideUnauthorizedError(): void {
    toast.dismiss(this._unauthorizedToastId);
  }

  public showError(error: any): void {
    const message = this.getErrorMessage(error);

    if (message === null) {
      return;
    }

    toast.error(message);
  }

  public showSuccess(message: string): void {
    toast.success(<Typography>{message}</Typography>, {
      position: 'top-center',
      autoClose: 1500,
      closeButton: false,
      closeOnClick: false,
      draggable: false,
      hideProgressBar: true,
      transition: Slide,
    });
  }

  public showTradeConfirmation(data: OrderResponse): void {
    const { error, message } = data;
    if (!error && !message.Message) {
      const { Order } = message;
      const { symbol, side, executed_avg_price, executed_amount, exchange } = Order;
      toast.success(
        <TradeConfirmationToast side={side} price={executed_avg_price || ''} amount={executed_amount || ''} exchange={exchange} symbol={symbol} />,
        {
          position: 'top-center',
          autoClose: 3000,
          closeButton: false,
          closeOnClick: false,
          draggable: false,
          className: 'flux-trade-confirmation',
          transition: Slide,
        }
      );
    } else if (error && message.Message) {
      toast.error(<TradeErrorToast message={message.Message ?? 'Oops, unable to place trade'} />, {
        position: 'top-center',
        autoClose: 3000,
        closeButton: false,
        closeOnClick: false,
        draggable: false,
        className: 'flux-trade-unfilled',
        transition: Slide,
      });
    }
  }

  public showV2TradeConfirmation(data: OtcQuoteOrderResponse): void {
    const { code, message, order_type } = data;
    if (!code && !message) {
      const { symbol, side, executed_avg_price, executed_amount, exchange, price, amount } = data;

      if (order_type === OtcOrderType.limit && Number(executed_amount) < Number(amount)) {
        toast.success(
          <LimitTradeConfirmationToast side={side} amount={amount} price={price} exchange={exchange} symbol={symbol} />,
          confirmationToastOptions
        );
      } else {
        toast.success(
          <TradeConfirmationToast side={side} price={executed_avg_price || ''} amount={executed_amount || ''} exchange={exchange} symbol={symbol} />,
          confirmationToastOptions
        );
      }
    } else if (code && message) {
      toast.error(<TradeErrorToast message={message ?? 'Oops, unable to place trade'} />, {
        position: 'top-center',
        autoClose: 1500,
        closeButton: false,
        closeOnClick: false,
        draggable: false,
        className: 'flux-trade-unfilled',
        hideProgressBar: true,
        transition: Slide,
      });
    }
  }

  public showAlertDismissedConfirmation(): void {
    toast.success(<Typography>Alert has been dismissed</Typography>, {
      position: 'top-center',
      autoClose: 1500,
      closeButton: false,
      closeOnClick: false,
      draggable: false,
      transition: Slide,
    });
  }

  public showQuoteBookTradeConfirmation(order: Order): void {
    const { symbol, side, executed_avg_price, executed_amount, exchange } = order;
    const isCurrentUser = order.user?.id === this._currentUserId;
    const options: ToastOptions = isCurrentUser
      ? { ...confirmationToastOptions, autoClose: false, closeButton: true }
      : { ...confirmationToastOptions, autoClose: 5000 };
    toast.success(
      <QuoteBookTradeConfirmationToast
        side={side}
        price={executed_avg_price || ''}
        amount={executed_amount || ''}
        exchange={exchange}
        symbol={symbol}
      />,
      options
    );
  }

  public showCancelOrderToast(data: OtcCancelOrderResponse): void {
    const { id } = data;
    toast.success(<OrderCancelToast id={id} />);
  }

  public showErrorMessage(message: string): void {
    toast.error(message, { autoClose: 5000, hideProgressBar: true, toastId: 'error-toast' });
  }

  public showCustomToast(content: JSX.Element, options: Partial<ToastOptions> = {}) {
    const defaults: Partial<ToastOptions> = {
      position: 'top-center',
      autoClose: 1500,
      closeButton: true,
      closeOnClick: false,
      draggable: false,
      transition: Slide,
    };
    toast.success(content, { ...defaults, ...options });
  }

  private getErrorMessage(error: any): string | null {
    if (error?.name === 'Cancel') {
      return null;
    }

    const defaultMessage = 'Sorry, something went wrong';

    if (!error) {
      return defaultMessage;
    }

    if (error.status === 401 || error.status === 422) {
      return null;
    }

    if (error.message && _.isString(error.message)) {
      return error.message;
    }

    if (_.isString(error)) {
      return error;
    }

    if (error.response) {
      if (error.response.title) {
        return error.response.title;
      } else if (_.isString(error.response)) {
        return error.response;
      } else if (error.response.errors) {
        const apiErrors = Object.values(error.response.errors);
        if (apiErrors.length > 0) {
          return apiErrors.join(',');
        }
      }
    }

    return defaultMessage;
  }
}

const toastifyService = new ToastifyService();
export { toastifyService };
